import { getPlantsOptions } from "@sunner-cloud/api-client";
import { useQuery } from "@tanstack/react-query";
import { useMatches, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { BreadcrumbItem } from "@/components/molecules/Breadcrumb";
import { DEFAULT_DEVICES_TAB } from "@/constants/deviceConstants";

// Rutas que aportan crumb navegable; su fullPath es el destino al hacer clic.
type CrumbTo =
  | "/plants"
  | "/plants/$plantId"
  | "/plants/$plantId/ncu/$ncuName"
  | "/plants/$plantId/ncu/$ncuName/tcu/$tcuName"
  | "/plants/$plantId/ncu/$ncuName/hsu/$hsuName";

const resolvePlantCrumb = (args: {
  navigate: ReturnType<typeof useNavigate>;
  plantsData: { plants: { id: string; name: string }[] } | undefined;
  isPlantsPending: boolean;
  plantId: string | undefined;
  isLast: boolean;
}): BreadcrumbItem | null => {
  const { navigate, plantsData, isPlantsPending, plantId, isLast } = args;
  if (isPlantsPending) {
    return { label: "", loading: true };
  }
  const name = plantsData?.plants.find((plant) => plant.id === plantId)?.name;
  // Mismo `tab` por defecto que fija useNavigateToPlantDevices (origen único).
  const onClick =
    isLast || plantId == null
      ? undefined
      : () =>
          void navigate({
            to: "/plants/$plantId",
            params: { plantId },
            search: { tab: DEFAULT_DEVICES_TAB, available: undefined },
          });
  return name != null ? { label: name, onClick } : null;
};

/**
 * Deriva las migas de la cadena de rutas activa (`useMatches`). Cada ruta que
 * aporta una miga declara `staticData.crumb` (ver `routeTree.ts`); aquí se
 * recorre esa cadena y se resuelve la etiqueta de cada nivel: "Plantas" por
 * i18n, NCU/TCU/HSU desde los params y el nombre de planta por `plantId` contra
 * la lista de plantas (caché compartida con Home/listado). Mientras el nombre
 * de planta carga se muestra un skeleton; si la planta no existe se descarta ese
 * crumb. El último nivel es el actual: no lleva `onClick` (lo marca `Breadcrumb`).
 * Fuera de las rutas con crumb no hay matches y devuelve `[]`.
 */
export const useBreadcrumbs = (): BreadcrumbItem[] => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Deja el `search` fuera de la proyección: con `structuralSharing`, cambiar de
  // tab reutiliza la referencia y evita re-renderizar el Topbar.
  const crumbMatches = useMatches({
    select: (matches) =>
      matches
        .filter((match) => match.staticData.crumb != null)
        .map((match) => {
          // `structuralSharing` exige un resultado serializable; el tipo de
          // `params` incluye ramas no serializables, así que se acota al plano
          // de strings que la miga realmente lee.
          const params: {
            plantId?: string;
            ncuName?: string;
            tcuName?: string;
            hsuName?: string;
          } = match.params;
          return {
            crumb: match.staticData.crumb,
            fullPath: match.fullPath,
            params,
          };
        }),
    structuralSharing: true,
  });

  // La query del nombre de planta solo se habilita si hay un crumb de planta en
  // la cadena. En navegación normal ya está en caché; en entrada directa puede
  // disparar el fetch.
  const hasPlantName = crumbMatches.some(
    (match) => match.crumb === "plantName",
  );
  const { data: plantsData, isPending: isPlantsPending } = useQuery({
    ...getPlantsOptions(),
    enabled: hasPlantName,
  });

  const lastIndex = crumbMatches.length - 1;

  const items = crumbMatches.map((match, index): BreadcrumbItem | null => {
    const { params } = match;
    // El tipo de `fullPath` incluye variantes con trailing slash que `to` no
    // admite, de ahí el cast al conjunto de rutas que aportan crumb.
    const onClick =
      index === lastIndex
        ? undefined
        : () =>
            void navigate({
              to: match.fullPath as CrumbTo,
              params,
            });

    switch (match.crumb) {
      case "plants":
        return { label: t("plants.title"), onClick };
      case "plantName":
        return resolvePlantCrumb({
          navigate,
          plantsData,
          isPlantsPending,
          plantId: params.plantId,
          isLast: index === lastIndex,
        });
      case "ncu":
        return { label: params.ncuName ?? "", onClick };
      case "tcu":
        return { label: params.tcuName ?? "", onClick };
      case "hsu":
        return { label: params.hsuName ?? "", onClick };
      default:
        return null;
    }
  });

  const breadcrumbs = items.filter(
    (item): item is BreadcrumbItem =>
      item != null && (item.loading === true || item.label.trim() !== ""),
  );

  return breadcrumbs.length > 1 ? breadcrumbs : [];
};
